/**
 * ClaimCorrectionModals — les deux gestes de correction d'un sinistre.
 *
 * Pourquoi ce fichier. Le workflow sinistre ne savait ni affecter un garage
 * après la déclaration, ni défaire une étape validée par erreur. Les deux
 * manques ont été constatés en production (2026-09-04). Ils sont regroupés ici
 * parce qu'ils répondent à la même question — « je me suis trompé, comment je
 * corrige ? » — et qu'ils partagent la même discipline : rien ne s'efface en
 * silence, tout est journalisé côté serveur.
 *
 * - ClaimGarageAssignModal : affecte ou change le garage, à n'importe quel
 *   moment du dossier. Ne touche jamais au statut.
 * - ClaimRollbackModal     : défait la dernière étape, motif obligatoire, en
 *   annonçant à l'avance ce qui sera effacé.
 */

import React, { useState, useEffect } from 'react';
import { X, Wrench, Undo2, AlertTriangle, Loader2, Building2, PenLine } from 'lucide-react';
import api, { claimAPI } from '../../api.js';
import { useFormat } from '../../i18n/format.js';

// ---------------------------------------------------------------------------
// Affecter / changer le garage
// ---------------------------------------------------------------------------

export function ClaimGarageAssignModal({ claim, isOpen, onClose, onDone, onMessage }) {
  const { t } = useFormat();
  const [garages, setGarages] = useState([]);
  const [loadingGarages, setLoadingGarages] = useState(false);
  const [mode, setMode] = useState('supplier'); // 'supplier' | 'manual'
  const [supplierId, setSupplierId] = useState('');
  const [manual, setManual] = useState({ garage_name: '', garage_phone: '', garage_email: '', garage_address: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSupplierId(claim?.garage_supplier_id ? String(claim.garage_supplier_id) : '');
    setMode(claim?.garage_supplier_id || !claim?.garage_name ? 'supplier' : 'manual');
    setManual({
      garage_name: claim?.garage_supplier_id ? '' : (claim?.garage_name || ''),
      garage_phone: claim?.garage_supplier_id ? '' : (claim?.garage_phone || ''),
      garage_email: claim?.garage_supplier_id ? '' : (claim?.garage_email || ''),
      garage_address: claim?.garage_supplier_id ? '' : (claim?.garage_address || ''),
    });

    let cancelled = false;
    setLoadingGarages(true);
    // Filtrage côté serveur : plus sûr qu'un filtre local sur une liste que le
    // data layer tronque silencieusement à 100 lignes.
    api.supplier.getAll({ category: 'garage' })
      .then(res => { if (!cancelled) setGarages(res?.data || []); })
      .catch(() => { if (!cancelled) setGarages([]); })
      .finally(() => { if (!cancelled) setLoadingGarages(false); });
    return () => { cancelled = true; };
  }, [isOpen, claim]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setError(null);
    const payload = mode === 'supplier'
      ? { garage_supplier_id: supplierId ? parseInt(supplierId, 10) : null }
      : { ...manual };

    if (mode === 'supplier' && !payload.garage_supplier_id) {
      setError(t('claims.garage.assignPickRequired'));
      return;
    }
    if (mode === 'manual' && !manual.garage_name.trim()) {
      setError(t('claims.garage.assignNameRequired'));
      return;
    }

    setBusy(true);
    try {
      const res = await claimAPI.assignGarage(claim.id, payload);
      onMessage?.({
        title: t('claims.messages.successTitle'),
        message: res?.garage?.previous
          ? t('claims.garage.assignChanged', { name: res.garage.garage_name || '-' })
          : t('claims.garage.assignDone', { name: res?.garage?.garage_name || '-' }),
        type: 'success',
      });
      onDone?.();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black bg-opacity-60 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
            <Wrench className="w-5 h-5 text-gray-600" />
            {t('claims.garage.assignTitle')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label={t('common.actions.close')}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-gray-600">{t('claims.garage.assignHint')}</p>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode('supplier')}
              className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-sm font-medium transition ${
                mode === 'supplier' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Building2 className="w-4 h-4" />
              {t('claims.garage.assignFromSuppliers')}
            </button>
            <button
              type="button"
              onClick={() => setMode('manual')}
              className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-sm font-medium transition ${
                mode === 'manual' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <PenLine className="w-4 h-4" />
              {t('claims.garage.assignManual')}
            </button>
          </div>

          {mode === 'supplier' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('claims.garage.assignPick')}
              </label>
              <select
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                disabled={loadingGarages}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{loadingGarages ? t('common.states.loading') : t('claims.garage.assignPickPlaceholder')}</option>
                {garages.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.name}{g.city ? ` — ${g.city}` : ''}
                  </option>
                ))}
              </select>
              {!loadingGarages && garages.length === 0 && (
                <p className="mt-1 text-xs text-amber-600">{t('claims.garage.assignNoSupplier')}</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('claims.garage.assignName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={manual.garage_name}
                  onChange={(e) => setManual({ ...manual, garage_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="tel"
                  value={manual.garage_phone}
                  onChange={(e) => setManual({ ...manual, garage_phone: e.target.value })}
                  placeholder={t('claims.garage.assignPhone')}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="email"
                  value={manual.garage_email}
                  onChange={(e) => setManual({ ...manual, garage_email: e.target.value })}
                  placeholder={t('claims.garage.assignEmail')}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <input
                type="text"
                value={manual.garage_address}
                onChange={(e) => setManual({ ...manual, garage_address: e.target.value })}
                placeholder={t('claims.garage.assignAddress')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
        </div>

        <div className="flex gap-3 border-t border-gray-200 px-5 py-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
          >
            {t('common.actions.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={busy}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-60"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('common.actions.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Revenir à l'étape précédente
// ---------------------------------------------------------------------------

export function ClaimRollbackModal({ claim, isOpen, onClose, onDone, onMessage }) {
  const { t } = useFormat();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !claim?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setReason('');
    claimAPI.getPreviousStep(claim.id)
      .then(res => { if (!cancelled) setPreview(res?.data || null); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, claim]);

  if (!isOpen) return null;

  const stepLabel = (step) => t(`claims.steps.${step}`, { defaultValue: step });
  const statusLabel = (status) => t(`common.workflow.statuses.claim.${status}`, { defaultValue: status });
  // Le serveur rend des noms de colonnes ; l'utilisateur lit une liste de données.
  // À défaut de libellé, on montre le nom brut plutôt que rien.
  const fieldLabel = (field) => t(`claims.fields.${field}`, { defaultValue: field });

  const handleConfirm = async () => {
    setError(null);
    if (reason.trim().length < 3) {
      setError(t('claims.rollback.reasonRequired'));
      return;
    }
    setBusy(true);
    try {
      const res = await claimAPI.rollbackStep(claim.id, {
        reason: reason.trim(),
        expectedStep: preview?.step_name,
      });
      const info = res?.rollback;
      onMessage?.({
        title: t('claims.messages.successTitle'),
        message: t('claims.rollback.done', {
          step: stepLabel(info?.undone_step || preview?.step_name),
          status: statusLabel(info?.to_status || preview?.target_status),
        }) + (info?.warning ? ` — ${info.warning}` : ''),
        type: info?.warning ? 'warning' : 'success',
      });
      onDone?.();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black bg-opacity-60 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
            <Undo2 className="w-5 h-5 text-amber-600" />
            {t('claims.rollback.title')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label={t('common.actions.close')}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('common.states.loading')}
            </div>
          )}

          {!loading && preview && !preview.can_rollback && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-700">
              {preview.reason || t('claims.rollback.notPossible')}
            </div>
          )}

          {!loading && preview?.can_rollback && (
            <>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                <p className="font-semibold">
                  {t('claims.rollback.willUndo', { step: stepLabel(preview.step_name) })}
                </p>
                <p className="mt-1">
                  {t('claims.rollback.willReturn', {
                    from: statusLabel(preview.current_status),
                    to: statusLabel(preview.target_status),
                  })}
                </p>
              </div>

              {preview.will_clear?.length > 0 && (
                <div className="rounded-lg border border-gray-200 px-3 py-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                    {t('claims.rollback.willClear')}
                  </p>
                  <ul className="text-xs text-gray-600 space-y-0.5">
                    {preview.will_clear.map(f => (
                      <li key={f} className="flex items-start gap-1.5">
                        <span className="text-gray-400">•</span>
                        <span className="break-words">{fieldLabel(f)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {preview.warning && (
                <div className="flex items-start gap-2 rounded-lg border border-orange-300 bg-orange-50 px-3 py-2 text-sm text-orange-900">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{preview.warning}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('claims.rollback.reasonLabel')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder={t('claims.rollback.reasonPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">{t('claims.rollback.reasonHint')}</p>
              </div>
            </>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
        </div>

        <div className="flex gap-3 border-t border-gray-200 px-5 py-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
          >
            {t('common.actions.close')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={busy || loading || !preview?.can_rollback}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-semibold disabled:opacity-50"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('claims.rollback.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
